import { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { currency, fmtDate } from "../../lib/status";
import { toast } from "sonner";

export default function Wallet() {
    const { user, refreshMe } = useAuth();
    const [txns, setTxns] = useState([]);
    const [amount, setAmount] = useState(100);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const { data } = await api.get("/wallet/transactions");
        setTxns(data);
    };

    useEffect(() => {
        load();
    }, []);

    const topup = async () => {
        if (amount <= 0) return;
        setBusy(true);
        try {
            await api.post("/wallet/topup", { amount: Number(amount) });
            toast.success(`Wallet topped up by ${currency(amount, "AED")}`);
            await refreshMe();
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Top-up failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-8">
            <div className="lx-label text-[#B2912A]">Wallet</div>
            <h1 className="font-serif text-4xl text-[#180A21]">
                Your <span className="italic">balance</span>.
            </h1>

            <div className="grid lg:grid-cols-3 gap-6">
                <div
                    className="lg:col-span-2 relative overflow-hidden p-10 text-white"
                    style={{
                        background:
                            "linear-gradient(135deg, #180A21 0%, #2B1438 70%, #180A21 100%)",
                    }}
                    data-testid="wallet-card"
                >
                    <div className="absolute -right-10 -bottom-10 w-60 h-60 rounded-full bg-[#D4AF37]/10 blur-3xl" />
                    <div className="relative z-10">
                        <div className="lx-label text-[#D4AF37] mb-4">Available balance</div>
                        <div className="font-serif text-6xl text-[#D4AF37]">
                            {currency(user?.wallet_balance || 0, "AED")}
                        </div>
                        <div className="lx-label text-white/60 mt-6">
                            {user?.email}
                        </div>
                    </div>
                </div>

                <div className="lx-card p-7">
                    <div className="lx-label text-[#B2912A] mb-4">Top up</div>
                    <div className="grid grid-cols-3 gap-2 mb-4">
                        {[100, 250, 500].map((v) => (
                            <button
                                key={v}
                                onClick={() => setAmount(v)}
                                data-testid={`topup-quick-${v}`}
                                className={`py-2 text-sm border ${
                                    amount === v
                                        ? "border-[#D4AF37] bg-[#D4AF37]/10"
                                        : "border-[#E5E2DC]"
                                }`}
                            >
                                {v}
                            </button>
                        ))}
                    </div>
                    <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        data-testid="topup-amount-input"
                        className="w-full p-3 border border-[#E5E2DC] focus:border-[#D4AF37] outline-none mb-3"
                    />
                    <button
                        data-testid="topup-btn"
                        onClick={topup}
                        disabled={busy}
                        className="lx-btn-gold w-full py-3 disabled:opacity-50"
                    >
                        Top up wallet
                    </button>
                </div>
            </div>

            <div className="lx-card">
                <div className="p-6 border-b border-[#F3F2F0] lx-label text-[#B2912A]">
                    Transactions
                </div>
                {txns.length === 0 ? (
                    <div className="p-12 text-center text-[#555]">No transactions yet.</div>
                ) : (
                    <div className="divide-y divide-[#F3F2F0]">
                        {txns.map((t) => (
                            <div
                                key={t.id}
                                className="p-5 flex items-center justify-between"
                                data-testid={`txn-row-${t.id}`}
                            >
                                <div>
                                    <div className="lx-label text-[#180A21]">{t.type}</div>
                                    <div className="text-xs text-[#555] mt-1">
                                        {fmtDate(t.created_at)}
                                    </div>
                                </div>
                                <div
                                    className={`font-serif text-xl ${
                                        t.amount > 0 ? "text-[#34C759]" : "text-[#FF2D55]"
                                    }`}
                                >
                                    {t.amount > 0 ? "+" : ""}
                                    {currency(t.amount, "AED")}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
